import { SkyDialog } from "@skypointcloud/skypoint-ui";
import type { ScheduleSummary } from "@shared/types/schedules";
import { useDeleteSchedule } from "@/hooks/use-schedules";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";

/** The dialog's question, naming the schedule (#1016). */
function deleteScheduleTitle(name: string): string {
  return `Delete the schedule ${name}?`;
}

/** What a delete does and does not touch, under the question. */
const DELETE_SCHEDULE_DESCRIPTION =
  "This cannot be undone. Its connections and dbt jobs are kept, but no longer run on this schedule. Runs already recorded stay in the run history.";

/**
 * Delete on a schedule asks first (#1016, found by QA: the row menu deleted at once). Nothing is
 * sent until the person selects Delete schedule; Keep schedule, Escape or the close button leave
 * it as it was. Built like the connection's delete dialog (`DeleteConnectionDialog` in
 * `client/src/pages/connections/list.tsx`): it closes on success and stays open with the reason on
 * a failure. Only a holder of `schedules.delete` (Instance admin and above since #1263) reaches it.
 */
export function DeleteScheduleDialog({
  target,
  onClose,
}: {
  target: ScheduleSummary | null;
  onClose: () => void;
}) {
  const remove = useDeleteSchedule();
  const notice = useNotice();
  const deleteError = useInlineError();
  const close = (): void => {
    deleteError.clear();
    onClose();
  };
  return (
    <SkyDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      // The dialog would close itself once the primary action returns, taking a failure with it.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="small"
      testId="delete-schedule-dialog"
      title={deleteScheduleTitle(target?.name ?? "")}
      description={DELETE_SCHEDULE_DESCRIPTION}
      primaryAction={{
        label: "Delete schedule",
        destructive: true,
        loading: remove.isPending,
        onClick: () => {
          if (!target) return;
          deleteError.clear();
          remove.mutate(target.id, {
            onSuccess: () => {
              close();
              // After the close, so the notice is not inside the modal's hidden window.
              notice.success(`${target.name} was deleted.`);
            },
            onError: (error) => deleteError.show(error, "The schedule could not be deleted."),
          });
        },
      }}
      secondaryAction={{ label: "Keep schedule", onClick: close }}
    >
      <InlineErrorBar
        error={deleteError.error}
        title={deleteError.error?.retryable ? undefined : "We could not delete this schedule"}
        testId="delete-schedule-error"
      />
    </SkyDialog>
  );
}
